import { useEffect } from 'react';

/**
 * GoogleAnalytics — injects the GA4 gtag.js only when a measurement ID is set
 * via `REACT_APP_GA_MEASUREMENT_ID` in the frontend `.env`.
 *
 * How to activate:
 *   1. Open `frontend/.env`
 *   2. Set `REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX` (your GA4 measurement ID)
 *   3. Redeploy
 *
 * If the ID is empty or not a valid `G-...` value, nothing is injected — the
 * site keeps a clean, tracker-free HTML (privacy-first default).
 */
export const GoogleAnalytics = () => {
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;

    useEffect(() => {
        // GA4 loader — only inject if a valid measurement ID is provided.
        const valid = typeof gaId === 'string' && /^G-[A-Z0-9]{6,}$/.test(gaId);
        if (!valid) return;
        if (document.querySelector(`script[data-ga="${gaId}"]`)) return; // already injected

        // 1. Async loader script
        const loader = document.createElement('script');
        loader.async = true;
        loader.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        loader.setAttribute('data-ga', gaId);
        loader.setAttribute('data-testid', 'ga4-loader-script');
        document.head.appendChild(loader);

        // 2. gtag config
        const config = document.createElement('script');
        config.setAttribute('data-testid', 'ga4-config-script');
        config.text = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = gtag;
            gtag('js', new Date());
            gtag('config', '${gaId}', { anonymize_ip: true });
        `;
        document.head.appendChild(config);
    }, [gaId]);

    return null;
};

export default GoogleAnalytics;
